import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
	Card,
	Crumbs,
	DASH,
	ErrorLine,
	Loading,
	MonthStepper,
	Tile,
	full,
	fyOf,
	groupOf,
	href,
	linkCls,
	monthLabel,
	short,
	snCls,
	sumOrNull,
	useMatrix,
} from './shared';

/* ── Month: every site in one wage month ────────────────────────────────────── */

export const MonthView: React.FC<{ fy: string; month: string }> = ({ fy, month }) => {
	const { loading, error, sites, summarySites, cell } = useMatrix(fy);
	const paid = sites
		.filter(s => cell(s, month))
		.sort((a, b) => (cell(b, month)!.net_pay || 0) - (cell(a, month)!.net_pay || 0));
	const missing = sites.filter(s => !cell(s, month));
	const total = sumOrNull(paid.map(s => cell(s, month)!.net_pay));
	const people = paid.reduce((a, s) => a + cell(s, month)!.people, 0);
	const max = Math.max(1, ...paid.map(s => cell(s, month)!.net_pay || 0));

	return (
		<>
			<Crumbs items={[{ label: 'Payroll', to: href({ fy }) }, { label: monthLabel(month) }]} />
			<div className='flex items-end justify-between mb-5'>
				<div>
					<h1 className='text-2xl font-semibold text-gray-900'>Wage month {monthLabel(month)}</h1>
					<p className='text-sm text-gray-500'>
						Every site for this month · click a site to see each person
					</p>
				</div>
				<MonthStepper month={month} to={m => href({ fy: fyOf(m), month: m })} />
			</div>
			{loading && <Loading />}
			{error && <ErrorLine msg={error} />}
			{!loading && (
				<>
					<div className='grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4'>
						<Tile label='Net pay, all sites' value={short(total)} />
						<Tile label='People' value={people ? people.toLocaleString('en-IN') : DASH} />
						<Tile label='Sites with a sheet' value={String(paid.length)} />
						<Tile
							label='Sites with no sheet'
							value={String(missing.length)}
							sub={missing.slice(0, 3).join(', ') + (missing.length > 3 ? '…' : '')}
						/>
					</div>
					<Card>
						<table className='w-full text-[13px]'>
							<thead className='text-gray-500 border-b border-gray-100'>
								<tr>
									<th className={snCls}>#</th>
									<th className='text-left px-4 py-2 font-medium'>Site</th>
									<th className='text-left px-3 py-2 font-medium w-[30%]'>Share of net pay</th>
									<th className='text-right px-3 py-2 font-medium'>People</th>
									<th className='text-right px-3 py-2 font-medium'>Net pay</th>
									<th className='text-right px-3 py-2 font-medium'>PF employee</th>
									<th className='text-right px-3 py-2 font-medium'>ESIC employee</th>
									<th className='text-right px-4 py-2 font-medium'>Versions</th>
								</tr>
							</thead>
							<tbody>
								{paid.map((s, i) => {
									const c = cell(s, month)!;
									return (
										<tr key={s} className='border-t border-gray-100 hover:bg-primary-50/50'>
											<td className={snCls}>{i + 1}</td>
											<td className='px-4 py-2'>
												<Link
													to={href({ fy, site: s, month })}
													className={`${linkCls} font-medium inline-flex items-center gap-1`}
												>
													{s} <ChevronRight className='w-3.5 h-3.5' />
												</Link>
												<div className='text-[11px] text-gray-400'>{groupOf(s)}</div>
											</td>
											<td className='px-3 py-2'>
												<div className='flex items-center gap-2'>
													<div className='flex-1 h-2 bg-gray-100 rounded-full overflow-hidden'>
														<div
															className='h-full bg-primary-500 rounded-full'
															style={{ width: `${((c.net_pay || 0) / max) * 100}%` }}
														/>
													</div>
													<span className='text-[11px] text-gray-500 w-10 text-right'>
														{total ? `${(((c.net_pay || 0) / total) * 100).toFixed(0)}%` : DASH}
													</span>
												</div>
											</td>
											<td className='px-3 py-2 text-right'>{c.people}</td>
											<td className='px-3 py-2 text-right font-semibold'>{full(c.net_pay)}</td>
											<td
												className={`px-3 py-2 text-right ${c.pf_ee === null ? 'text-gray-300' : ''}`}
											>
												{full(c.pf_ee)}
											</td>
											<td
												className={`px-3 py-2 text-right ${c.esi_ee === null ? 'text-gray-300' : ''}`}
											>
												{full(c.esi_ee)}
											</td>
											<td className='px-4 py-2 text-right text-gray-500'>{c.versions_seen}</td>
										</tr>
									);
								})}
								{missing.map((s, i) => (
									<tr key={s} className='border-t border-gray-100 text-gray-300'>
										<td className={snCls}>{paid.length + i + 1}</td>
										<td className='px-4 py-2'>
											<Link to={href({ fy, site: s })} className='hover:underline'>
												{s}
											</Link>
										</td>
										<td className='px-3 py-2' colSpan={6}>
											No sheet this month
										</td>
									</tr>
								))}
								{summarySites
									.filter(s => cell(s, month))
									.map(s => (
										<tr
											key={s}
											className='border-t border-gray-200 bg-gray-50 text-gray-500 italic'
										>
											<td className={snCls}>{DASH}</td>
											<td className='px-4 py-2'>
												<Link to={href({ fy, site: s, month })} className={`${linkCls} not-italic`}>
													{s}
												</Link>
												<div className='text-[11px]'>cross-check, not added</div>
											</td>
											<td />
											<td className='px-3 py-2 text-right'>{cell(s, month)!.people}</td>
											<td className='px-3 py-2 text-right'>{full(cell(s, month)!.net_pay)}</td>
											<td colSpan={3} />
										</tr>
									))}
							</tbody>
							<tfoot className='border-t-2 border-gray-200 font-semibold'>
								<tr>
									<td />
									<td className='px-4 py-2'>Total · {paid.length} sites</td>
									<td className='px-3 py-2 text-[11px] text-gray-500 font-normal'>
										cross-check row not added
									</td>
									<td className='px-3 py-2 text-right'>{people.toLocaleString('en-IN')}</td>
									<td className='px-3 py-2 text-right'>{full(total)}</td>
									<td className='px-3 py-2 text-right'>
										{full(sumOrNull(paid.map(s => cell(s, month)!.pf_ee)))}
									</td>
									<td className='px-3 py-2 text-right'>
										{full(sumOrNull(paid.map(s => cell(s, month)!.esi_ee)))}
									</td>
									<td />
								</tr>
							</tfoot>
						</table>
					</Card>
				</>
			)}
		</>
	);
};
